import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import api from '../lib/api';
import {
  LayoutDashboard, FileText, ClipboardList, Building2, DoorOpen, Users, Bot,
  TrendingUp, LogOut, Menu, X, ChevronRight, ChevronLeft, ChevronDown,
  Calendar, Package, Upload, HelpCircle, Shield, BarChart3, Activity,
  Database, BookOpen, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LanguageToggle from './LanguageToggle';
import ThemeToggle from './ThemeToggle';
import GlobalSearch from './GlobalSearch';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import InstallPrompt from './InstallPrompt';
import TopBarIcons from './TopBarIcons';
import { toast } from 'sonner';

const LOGO_BLACK_KEY = '/logo-black.png';
const LOGO_WHITE_KEY = '/logo-white.png';

// All nav items by path (single source of truth)
const ALL_ITEMS = {
  '/dashboard':      { labelKey: 'nav.board', subtitleKey: 'nav.boardSubtitle', icon: LayoutDashboard },
  '/my-maintenance': { labelKey: 'nav.myMaintenance', subtitleKey: 'nav.myMaintenanceSubtitle', icon: LayoutDashboard },
  '/work-requests':  { labelKey: 'nav.requests', subtitleKey: 'nav.requestsSubtitle', icon: FileText },
  '/work-orders':    { labelKey: 'nav.workOrders', subtitleKey: 'nav.workOrdersSubtitle', icon: ClipboardList },
  '/inventory':      { labelKey: 'nav.inventory', icon: Package },
  '/pm-plans':       { labelKey: 'nav.pmScheduling', icon: Calendar },
  '/reports':        { labelKey: 'nav.reports', icon: BarChart3 },
  '/cost-analytics': { labelKey: 'nav.costAnalytics', icon: TrendingUp },
  '/branches':       { labelKey: 'nav.branches', icon: Building2 },
  '/rooms':          { labelKey: 'nav.roomsAssets', icon: DoorOpen },
  '/import-codes':   { labelKey: 'nav.importCodes', icon: Upload },
  '/users':          { labelKey: 'nav.users', icon: Users },
  '/sla-policies':   { labelKey: 'nav.slaPolicies', icon: Shield },
  '/sla-compliance': { labelKey: 'nav.slaCompliance', icon: BarChart3 },
  '/system-status':  { labelKey: 'nav.systemStatus', icon: Activity },
  '/data-checks':    { labelKey: 'nav.dataChecks', icon: Database },
  '/help':           { labelKey: 'nav.help', icon: BookOpen },
};

// Requester special item
const REQUESTER_ITEM = { '/work-requests': { labelKey: 'nav.newRequest', subtitleKey: 'nav.newRequestSubtitle', icon: FileText } };

// Technician special item — technician-initiated maintenance
const TECHNICIAN_ITEM = { '/work-requests': { labelKey: 'nav.reportFault', subtitleKey: 'nav.reportFaultSubtitle', icon: FileText } };

// Group definitions: which paths belong to which group
const GROUP_DEFS = [
  { key: 'operations', labelKey: 'nav.groupOperations', defaultOpen: true,
    paths: ['/dashboard', '/my-maintenance', '/work-requests', '/work-orders', '/inventory', '/pm-plans'] },
  { key: 'insights', labelKey: 'nav.groupInsights', defaultOpen: false,
    paths: ['/reports', '/cost-analytics'] },
  { key: 'configuration', labelKey: 'nav.groupConfiguration', defaultOpen: false,
    paths: ['/branches', '/rooms', '/import-codes', '/users'] },
  { key: 'system', labelKey: 'nav.groupSystem', defaultOpen: false,
    paths: ['/sla-policies', '/sla-compliance', '/system-status', '/data-checks', '/help'] },
];

// Which paths each role can see (flat lists)
const ROLE_PATHS = {
  admin: ['/dashboard','/work-requests','/work-orders','/pm-plans','/inventory','/sla-policies','/sla-compliance','/branches','/rooms','/users','/cost-analytics','/reports','/import-codes','/system-status','/data-checks','/help'],
  supervisor: ['/dashboard','/work-requests','/work-orders','/pm-plans','/inventory','/sla-policies','/sla-compliance','/rooms','/users','/cost-analytics','/reports','/help'],
  branch_manager: ['/dashboard','/work-requests','/work-orders','/inventory','/rooms','/users','/reports','/help'],
  technician: ['/dashboard', '/work-requests', '/help'],
  requester: ['/my-maintenance', '/work-requests', '/help'],
};

const SUPER_ADMIN_EMAIL = 'ahmad.disi@unknownroom.com';

function useGroupState(userId, groups, currentPath) {
  const storageKey = `sidebar_groups_${userId || 'anon'}`;

  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    // Defaults
    const defaults = {};
    groups.forEach(g => { defaults[g.key] = g.defaultOpen; });
    return defaults;
  });

  // Auto-expand the group containing the active path
  useEffect(() => {
    for (const g of groups) {
      const match = g.paths.some(p => currentPath === p || (p !== '/dashboard' && currentPath.startsWith(p)));
      if (match && !expanded[g.key]) {
        setExpanded(prev => ({ ...prev, [g.key]: true }));
        break;
      }
    }
  }, [currentPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(expanded)); } catch {}
  }, [expanded, storageKey]);

  const toggle = useCallback((key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return { expanded, toggle };
}

export default function DashboardLayout({ children }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const isRTL = i18n.language === 'ar';
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  const rolePaths = ROLE_PATHS[user?.role] || (user?.role === 'super_admin' ? ROLE_PATHS.admin : ROLE_PATHS.requester);
  const useGroups = !['technician', 'requester'].includes(user?.role);
  const mobileLogo = theme === 'dark' ? LOGO_WHITE_KEY : LOGO_BLACK_KEY;

  // Build grouped nav for roles with many items
  const navGroups = useGroups ? GROUP_DEFS.map(g => ({
    ...g,
    items: g.paths.filter(p => rolePaths.includes(p)).map(p => ({ path: p, ...ALL_ITEMS[p] })),
  })).filter(g => g.items.length > 0) : [];

  // Flat items for simple roles (requester/technician)
  const flatItems = !useGroups ? rolePaths.map(p => {
    if (p === '/work-requests') {
      if (user?.role === 'requester') return { path: p, ...REQUESTER_ITEM[p] };
      if (user?.role === 'technician') return { path: p, ...TECHNICIAN_ITEM[p] };
    }
    return { path: p, ...ALL_ITEMS[p] };
  }) : [];

  const { expanded, toggle } = useGroupState(user?.id, navGroups, location.pathname);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const renderNavLink = (item) => {
    const isActive = location.pathname === item.path ||
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.path.slice(1)}`}
        className={`sidebar-link flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
          isActive
            ? 'text-white bg-zinc-800 border-s-2 border-brand'
            : 'text-muted-foreground hover:text-white hover:bg-zinc-800/50'
        }`}
        onClick={() => setSidebarOpen(false)}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-brand' : ''}`} />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium block">{t(item.labelKey)}</span>
          {item.subtitleKey && (
            <span className="text-[10px] text-muted-foreground/70 block truncate">{t(item.subtitleKey)}</span>
          )}
        </div>
      </Link>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="dashboard-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed lg:static inset-y-0 z-50 w-64 bg-zinc-900 text-muted-foreground flex flex-col transition-transform duration-200 border-e border-zinc-800 ${
          isRTL ? 'end-0' : 'start-0'
        } ${sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-4">
          <img src={LOGO_WHITE_KEY} alt="Logo" className="w-10 h-10 object-contain" />
          <div className="flex-1 min-w-0">
            <h1 className="font-barlow font-bold text-base text-white tracking-tight leading-none truncate">{t('common.appName')}</h1>
            <span className="text-[10px] uppercase tracking-widest text-brand">{t('common.maintenance')}</span>
          </div>
          <button className="lg:hidden ms-auto text-muted-foreground hover:text-white" onClick={() => setSidebarOpen(false)} data-testid="sidebar-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <Separator className="bg-zinc-800" />

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto" data-testid="sidebar-nav">
          {useGroups ? (
            <div className="space-y-1">
              {navGroups.map((group) => {
                const isOpen = expanded[group.key] !== false;
                const hasActive = group.items.some(i =>
                  location.pathname === i.path || (i.path !== '/dashboard' && location.pathname.startsWith(i.path))
                );
                return (
                  <div key={group.key} data-testid={`nav-group-${group.key}`}>
                    <button
                      onClick={() => toggle(group.key)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-[11px] uppercase tracking-wider font-semibold transition-colors ${
                        hasActive && !isOpen ? 'text-brand' : 'text-zinc-500 hover:text-zinc-300'
                      }`}
                      data-testid={`nav-group-toggle-${group.key}`}
                    >
                      <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${isOpen ? '' : isRTL ? 'rotate-90' : '-rotate-90'}`} />
                      <span>{t(group.labelKey)}</span>
                    </button>
                    {isOpen && (
                      <div className="space-y-0.5 ps-1 pb-1">
                        {group.items.map(renderNavLink)}

                        {/* Super Admin items in System group */}
                        {group.key === 'system' && isSuperAdmin && (
                          <>
                            <Link
                              to="/moltbot"
                              data-testid="nav-moltbot"
                              className={`sidebar-link flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                location.pathname === '/moltbot'
                                  ? 'text-white bg-zinc-800 border-s-2 border-brand'
                                  : 'text-muted-foreground hover:text-white hover:bg-zinc-800/50'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <Bot className={`w-4 h-4 flex-shrink-0 ${location.pathname === '/moltbot' ? 'text-brand' : ''}`} />
                              {t('nav.moltbot')}
                            </Link>
                            <Link
                              to="/super-admin"
                              data-testid="nav-super-admin-console"
                              className={`sidebar-link flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors border border-red-500/30 ${
                                location.pathname.startsWith('/super-admin')
                                  ? 'text-red-500 bg-red-500/10'
                                  : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <Shield className="w-4 h-4 flex-shrink-0" />
                              {t('nav.superAdminConsole')}
                            </Link>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1">
              {flatItems.map(renderNavLink)}
            </div>
          )}
        </nav>

        <Separator className="bg-zinc-800" />

        {/* Footer */}
        <div className="px-3 py-4">
          <InstallPrompt />
          <div className="px-3 mb-3 flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle className="text-muted-foreground hover:text-white hover:bg-zinc-800" />
          </div>
          <div className="px-3 mb-3">
            <button onClick={() => setShortcutsOpen(true)} className="text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors flex items-center gap-1.5" data-testid="sidebar-shortcuts-hint">
              <kbd className="text-[10px] px-1 py-0.5 rounded bg-zinc-800 border border-zinc-700">?</kbd>
              <span>{t('shortcuts.hint')}</span>
            </button>
          </div>
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('common.signedInAs')}</p>
            <p className="text-sm text-white font-medium truncate">{user?.name}</p>
            <p className="text-xs text-brand capitalize">{t(`auth.${user?.role}`)}</p>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-zinc-800" onClick={() => setChangePasswordOpen(true)} data-testid="change-password-btn">
            <Lock className="w-4 h-4 me-2" />{t('auth.changePassword')}
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-zinc-800" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 me-2" />{t('common.signOut')}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 bg-card border-b border-border">
          <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-open-btn" className="text-muted-foreground hover:text-foreground">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <img src={mobileLogo} alt="Logo" className="w-7 h-7 object-contain" />
            <span className="font-barlow font-bold text-sm text-foreground tracking-tight">{t('common.appName')}</span>
          </div>
          <TopBarIcons />
          <ThemeToggle className="text-muted-foreground hover:text-foreground" />
          <LanguageToggle />
        </header>
        <header className="hidden lg:flex items-center gap-4 px-6 py-3 bg-card/50 border-b border-border">
          <div className="flex-1"><GlobalSearch /></div>
          <div className="flex items-center gap-2">
            <TopBarIcons />
            <Button variant="ghost" size="icon" onClick={() => setShortcutsOpen(true)} className="text-muted-foreground hover:text-foreground" title={t('shortcuts.title')} data-testid="help-shortcuts-btn">
              <HelpCircle className="w-5 h-5" />
            </Button>
            <ThemeToggle className="text-muted-foreground hover:text-foreground" />
            <LanguageToggle />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 bg-background">{children}</main>
      </div>

      <KeyboardShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError(t('auth.passwordMinLength')); return; }
    if (newPassword !== confirmPassword) { setError(t('auth.passwordsDoNotMatch')); return; }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword });
      toast.success(t('auth.passwordChangedSuccess'));
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.detail || t('auth.passwordChangeFailed'));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-barlow flex items-center gap-2"><Lock className="w-5 h-5" /> {t('auth.changePassword')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2"><Label>{t('auth.currentPassword')}</Label><Input type="password" value={currentPassword} onChange={(e) => { setCurrentPassword(e.target.value); setError(''); }} required data-testid="profile-current-password" /></div>
          <div className="space-y-2"><Label>{t('auth.newPassword')}</Label><Input type="password" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setError(''); }} required data-testid="profile-new-password" /></div>
          <div className="space-y-2"><Label>{t('auth.confirmPassword')}</Label><Input type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }} required data-testid="profile-confirm-password" /></div>
          {error && <p className="text-sm text-red-500" data-testid="profile-change-pw-error">{error}</p>}
          <p className="text-xs text-muted-foreground">{t('auth.passwordMinLengthHint')}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={loading} data-testid="profile-change-pw-submit">{loading ? t('common.loading') : t('auth.changePassword')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
